import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isNil } from 'lodash';

import type { AiAction } from '~/components/AiChat/types';
import { isDynamicClaimActionTaskData } from '~/components/AiChat/types';
import { findActionExposure, getActionExposureRef } from '~/components/AiChat/utils/actionExposure';
import { useClaim } from '~/components/ClaimContainer';
import type { FetchState } from '~/components/useDataFetcher/types';
import { dataFetchReducer, fetchData } from '~/components/useDataFetcher/utils';

// The template routes read exposure `0` as the claim itself, as the template picker's "General" option sends it.
const GENERAL_EXPOSURE_ID = 0;

export interface DocumentTemplateOption {
  doc_id: number;
  display_name?: string;
  type: string;
  document_type: string;
}

type TemplateList = FetchState<DocumentTemplateOption[]>;

interface TemplateListsCache {
  lists: Record<string, TemplateList>;
  request: (claimId: number, exposureId: number) => void;
}

const LOADING: TemplateList = { isLoading: true, isError: false, data: undefined };
const NOT_ASKED: TemplateList = { isLoading: false, isError: false, data: undefined };

const listKey = (claimId: number, exposureId: number) => `${claimId}/${exposureId}`;

const normalizeName = (name: unknown) =>
  String(name ?? '')
    .trim()
    .toLowerCase();

/**
 * Each list read once while the cache lives. A failed read is forgotten, so the next card or dialog to
 * need the list asks again. A read that lands after the cache unmounts sets nothing: React 18 ignores it.
 */
const useTemplateListsCache = (): TemplateListsCache => {
  const [lists, setLists] = useState<Record<string, TemplateList>>({});
  const requested = useRef(new Set<string>());

  const request = useCallback((claimId: number, exposureId: number) => {
    const key = listKey(claimId, exposureId);
    if (requested.current.has(key)) return;
    requested.current.add(key);
    // Asked again after a failure, the list is loading, not still failed.
    setLists((prev) => (prev[key] ? { ...prev, [key]: LOADING } : prev));

    // Reported without the error dialog: nobody asked for the list, and every card reads without it.
    void fetchData<DocumentTemplateOption[]>(
      `/api/v1/claims/${claimId}/documents/document_templates_and_bundles`,
      { params: { exposure_id: exposureId, is_enabled: true } },
      true,
      false
    ).then((action) => {
      const list = dataFetchReducer(LOADING, action);
      if (list.isError) requested.current.delete(key);
      setLists((prev) => ({ ...prev, [key]: list }));
    });
  }, []);

  return useMemo(() => ({ lists, request }), [lists, request]);
};

const TemplateListsContext = createContext<TemplateListsCache | undefined>(undefined);

/**
 * One read of the claim's template list per exposure, for every card under it that names a template:
 * the Conductor Hub's two panes, or a dialog's list and its preview. A dialog mounts its own, even
 * under the Hub's, so each open reads the list as it is now: a template disabled since the Hub loaded
 * is not previewed only for the action to refuse it.
 */
export const DocumentTemplatesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TemplateListsContext.Provider value={useTemplateListsCache()}>{children}</TemplateListsContext.Provider>
);

/** The templates the claim offers for an exposure, from the provider above; nothing is read without one. */
const useTemplateList = (claimId: number, exposureId: number | undefined, shouldFetch: boolean): TemplateList => {
  const cache = useContext(TemplateListsContext);
  // `request` is stable: keyed on the whole cache, a failed read would re-run this effect and ask again forever.
  const request = cache?.request;

  useEffect(() => {
    if (shouldFetch && request && !isNil(exposureId)) request(claimId, exposureId);
  }, [shouldFetch, request, claimId, exposureId]);

  if (!shouldFetch || !cache || isNil(exposureId)) return NOT_ASKED;
  return cache.lists[listKey(claimId, exposureId)] ?? LOADING;
};

export interface ActionDocumentTemplate {
  /** What the list is read for: `0` for the claim itself, unset for an exposure the claim does not carry. */
  exposureId?: number;
  /** The one template the card names; `null` while the list loads, and when none or several match. */
  template: DocumentTemplateOption | null;
  matches: DocumentTemplateOption[];
  isLoading: boolean;
  /** The list has been read, so no match means the claim offers no such template. */
  isListed: boolean;
}

/**
 * The template a create-document-from-template card names, chosen from the list the template picker
 * offers: by the card's template id, or else its document type and name (case and surrounding spaces
 * ignored), as the action resolves it. Nothing is read unless `shouldFetch`.
 */
export const useActionDocumentTemplate = (action: AiAction, shouldFetch = true): ActionDocumentTemplate => {
  const { claim } = useClaim({ claimRequired: true });
  const payload = isDynamicClaimActionTaskData(action.task_data) ? action.task_data.envelope?.payload : undefined;

  const exposureId = isNil(getActionExposureRef(action))
    ? GENERAL_EXPOSURE_ID
    : findActionExposure(action, claim?.exposures)?.id;
  const templates = useTemplateList(claim.id, exposureId, shouldFetch);

  // The id wins over the type and name, and names only a template that has both, as the action reads it.
  const matches = useMemo(() => {
    const options = (templates.data ?? []).filter((option) => option.type === 'document_template');
    if (!isNil(payload?.template_id)) {
      return options.filter(
        (option) => option.doc_id === Number(payload?.template_id) && option.display_name && option.document_type
      );
    }
    return options.filter(
      (option) =>
        option.document_type === payload?.document_type &&
        normalizeName(option.display_name) === normalizeName(payload?.template_name)
    );
  }, [templates.data, payload]);

  return {
    exposureId,
    template: matches.length === 1 ? matches[0] : null,
    matches,
    isLoading: templates.isLoading,
    isListed: !isNil(templates.data),
  };
};
